/**
 * BFF Subscriptions API response types (subscriptions/v1/bff/subscriptions)
 */

import type { BffCatalogSubscription } from '@/core/models/bff-catalog';
import type { BffBillingPeriod, BffPlanInterval } from '@/core/models/bff-purchasable-plan';

export enum BffSubscriptionStatus {
  Pending = 'pending',
  Active = 'active',
  Paused = 'paused',
  Stopped = 'stopped',
  PaymentFailed = 'payment_failed',
  Cancelled = 'cancelled',
  Expired = 'expired',
  PendingActivation = 'pending_activation',
}

export enum BffSubscriptionInterval {
  Weekly = 'weekly',
  Monthly = 'monthly',
  Quarterly = 'quarterly',
  Yearly = 'yearly',
  Permanent = 'permanent',
}

export type BffSubscriptionPlanType =
  | 'individual'
  | 'family'
  | 'smb'
  | 'b2b'
  | 'partner'
  | 'complimentary';

export type BffSubscriptionPaymentType =
  | 'recurring'
  | 'prepaid'
  | 'complimentary';

export type BffSubscriptionSeats = {
  total: number;
  used: number;
  available: number;
};

export type BffSubscriptionUserActionName =
  | 'pause'
  | 'resume'
  | 'cancel'
  | 'activate'
  | 'initiateTransfer'
  | 'revokeTransfer'
  | 'inviteMember';

/** Whether the caller may perform an action, with the BFF's denial reason (e.g. `subscription_not_active`). */
export type BffSubscriptionUserAction = {
  allowed: boolean;
  reason?: string | null;
};

export type BffSubscriptionUserActions = Partial<
  Record<BffSubscriptionUserActionName, BffSubscriptionUserAction>
>;

export type BffSubscription = {
  id: string;
  uuid?: string;
  productCode?: string | null;
  name: string;
  description?: string;
  /** Per billing-interval price (e.g. monthly). Canonical BFF field: `billingPrice`. */
  billingPrice?: number;
  /** Total price for the contracted interval (e.g. yearly total). Canonical BFF field: `contractPrice`. */
  contractPrice?: number;
  /** Per billing-interval price (e.g. monthly). */
  unitPrice: number;
  /** Discounted per billing-interval price when a promotion or loyalty discount applies. */
  discountedUnitPrice?: number | null;
  /** Total price for the contracted interval (e.g. yearly total). */
  intervalPrice?: number;
  /** Discounted total interval price when a promotion or loyalty discount applies. */
  discountedIntervalPrice?: number | null;
  currency: string;
  isActive: boolean;
  status: BffSubscriptionStatus | string;
  interval: BffSubscriptionInterval;
  billingInterval?: BffBillingPeriod | null;
  contractInterval?: BffPlanInterval;
  /** @deprecated Use {@link billingInterval}. */
  billingPeriod?: BffBillingPeriod | null;
  paymentType?: BffSubscriptionPaymentType | null;
  renewalDate?: string;
  startDate?: string | null;
  endDate?: string | null;
  /** When set and in the future, the subscription is still in its trial period. */
  trialEndDate?: string;
  thumbnailUrl?: string;
  thumbnailURL?: string;
  displayText?: string;
  displayAssets?: string[];
  role?: BffSubscriptionMemberRole | null;
  /** BFF may return either spelling; prefer isUserManageable when present. */
  isUserManagable?: boolean;
  isUserManageable?: boolean;
  /** When set (e.g. promo), account UI can show a success alert. */
  discountDescription?: string;
  /** Plan type from the BFF (lowercase wire values). */
  type?: BffSubscriptionPlanType | null;
  /** Seat usage summary; only present for admin callers of shareable plans. */
  seats?: BffSubscriptionSeats | null;
  /** Per-action permissions from the BFF; authoritative when present. */
  userActions?: BffSubscriptionUserActions | null;
};

export type BffSubscriptionWire = Omit<
  BffSubscription,
  'id' | 'interval' | 'status' | 'billingInterval' | 'contractInterval'
> & {
  id?: string;
  uuid?: string;
  interval?: string;
  status?: string;
  billingInterval?: string | null;
  contractInterval?: string;
  billingPeriod?: string | null;
};

export type BffSubscriptionMemberRole = 'admin' | 'member';

export type BffSubscriptionMemberStatus =
  | 'invited'
  | 'active'
  | 'transferred'
  | 'removed';

export type BffSubscriptionMember = {
  uuid: string;
  role: BffSubscriptionMemberRole;
  status: BffSubscriptionMemberStatus;
  name: string | null;
  email: string | null;
  activatedAt: string | null;
  invitedAt: string;
};

export type BffSubscriptionMembersResponse = {
  data: BffSubscriptionMember[];
};

export type BffSubscriptionMemberInvite = {
  memberUuid: string;
  code: string;
  expiresAt: string;
  email: string | null;
};

export type BffSubscriptionMemberInviteResponse = {
  data: BffSubscriptionMemberInvite;
};

/** Active user subscription (BFF user API) or pending product row from catalog. */
export type AccountSubscriptionPlanData = BffCatalogSubscription | BffSubscription;

export function isBffUserSubscription(
  s: AccountSubscriptionPlanData,
): s is BffSubscription {
  return 'status' in s;
}

export type BffSubscriptionsListResponse = {
  data: BffSubscription[];
};

export type BffSubscriptionDetailResponse = {
  data: BffSubscription;
};

export type BffSubscriptionRedeemResponse = {
  data: {
    subscriptionUuid: string;
  };
};

const BFF_SUBSCRIPTION_INTERVAL_VALUES = new Set<string>(
  Object.values(BffSubscriptionInterval),
);

/** Normalizes BFF subscription status for comparisons (API uses lowercase). */
export function normalizeBffSubscriptionStatus(status: string): string {
  return status.trim().toLowerCase();
}

/** Normalizes BFF subscription interval wire values (API uses lowercase). */
export function normalizeBffSubscriptionInterval(
  interval: string,
): BffSubscriptionInterval | string {
  const normalized = interval.trim().toLowerCase();

  if (normalized === 'lifetime') {
    return BffSubscriptionInterval.Permanent;
  }

  if (BFF_SUBSCRIPTION_INTERVAL_VALUES.has(normalized)) {
    return normalized;
  }

  return normalized;
}

function normalizeOptionalBillingPeriod(
  value: string | null | undefined,
): BffBillingPeriod | null | undefined {
  if (value == null) {
    return value;
  }

  const normalized = value.trim().toLowerCase();
  if (normalized === 'monthly' || normalized === 'yearly') {
    return normalized;
  }

  return normalized as BffBillingPeriod;
}

function normalizeOptionalPlanInterval(
  value: string | null | undefined,
): BffPlanInterval | undefined {
  if (value == null) {
    return undefined;
  }

  const normalized = normalizeBffSubscriptionInterval(value);
  if (typeof normalized === 'string' && BFF_SUBSCRIPTION_INTERVAL_VALUES.has(normalized)) {
    return normalized as BffPlanInterval;
  }

  return normalized as BffPlanInterval;
}

/** Normalizes a user-subscription wire row from the BFF. */
export function normalizeBffSubscription(wire: BffSubscriptionWire): BffSubscription {
  const id = String(wire.uuid ?? wire.id ?? '');
  const billingPrice = wire.billingPrice ?? wire.unitPrice ?? 0;
  const contractPrice = wire.contractPrice ?? wire.intervalPrice;
  const interval = normalizeBffSubscriptionInterval(
    String(wire.interval ?? wire.contractInterval ?? BffSubscriptionInterval.Monthly),
  ) as BffSubscriptionInterval;
  const billingInterval =
    normalizeOptionalBillingPeriod(wire.billingInterval ?? wire.billingPeriod) ?? null;
  const contractInterval =
    normalizeOptionalPlanInterval(wire.contractInterval ?? wire.interval) ?? interval;
  const status = normalizeBffSubscriptionStatus(
    String(wire.status ?? BffSubscriptionStatus.Active),
  );
  const isUserManageable = wire.isUserManageable ?? wire.isUserManagable;

  const normalized: BffSubscription = {
    ...wire,
    id,
    uuid: wire.uuid ?? id,
    billingPrice,
    unitPrice: billingPrice,
    status,
    interval,
    billingInterval,
    contractInterval,
    billingPeriod: billingInterval,
    thumbnailUrl: wire.thumbnailUrl ?? wire.thumbnailURL,
    isUserManageable,
  };

  if (typeof contractPrice === 'number') {
    normalized.contractPrice = contractPrice;
    normalized.intervalPrice = contractPrice;
  }

  return normalized;
}

/**
 * Cancelled or stopped lifecycle status (no further renewal).
 * Independent of {@link BffSubscription.isActive}: a cancelled plan can still grant access until `endDate`.
 */
export function isBffSubscriptionCancelled(
  subscription: Pick<BffSubscription, 'status'>,
): boolean {
  const status = normalizeBffSubscriptionStatus(String(subscription.status));
  return (
    status === 'stopped' ||
    status === 'cancelled' ||
    status === 'canceled'
  );
}

/**
 * Whether the subscription no longer grants access, so it should disappear from account UI.
 *
 * Field roles (they are independent — do not treat `status` alone as “ended”):
 * - `isActive`: BFF entitlement flag. `false` means the plan no longer grants access.
 * - `status`: lifecycle state (`active`, `paused`, `cancelled`, `payment_failed`, …).
 *   A plan can be `status: "cancelled"` and still `isActive: true` until the contracted
 *   period runs out (no further renewal, but access continues).
 * - `endDate`: when a cancelled/stopped plan finally loses access. Until this date passes,
 *   cancelled-but-active subscriptions stay in the list.
 * - `expired`: always treated as ended.
 */
export function hasBffSubscriptionEnded(
  subscription: Pick<BffSubscription, 'status' | 'isActive' | 'endDate'>,
  now: number = Date.now(),
): boolean {
  if (subscription.isActive === false) {
    return true;
  }

  const status = normalizeBffSubscriptionStatus(String(subscription.status));
  if (status === 'expired') {
    return true;
  }

  if (!isBffSubscriptionCancelled(subscription)) {
    return false;
  }

  const endDate = subscription.endDate?.trim();
  if (!endDate) {
    return true;
  }

  const endMs = Date.parse(endDate);
  return Number.isNaN(endMs) || endMs <= now;
}

/** Visible account subscriptions (excludes ended ones). Sort: still-entitled first, then active/paused, then latest renewal. */
export function listVisibleBffSubscriptions(
  subscriptions: BffSubscription[] | undefined,
): BffSubscription[] {
  if (subscriptions == null || subscriptions.length === 0) {
    return [];
  }

  return [...subscriptions]
    .filter(subscription => !hasBffSubscriptionEnded(subscription))
    .sort((a, b) => {
      // Prefer plans that still grant access (isActive can be true while status is cancelled).
      const aActive = a.isActive === false ? 1 : 0;
      const bActive = b.isActive === false ? 1 : 0;
      if (aActive !== bActive) return aActive - bActive;

      const aStatus = normalizeBffSubscriptionStatus(String(a.status));
      const bStatus = normalizeBffSubscriptionStatus(String(b.status));
      const aPrio = aStatus === 'active' || aStatus === 'paused' ? 0 : 1;
      const bPrio = bStatus === 'active' || bStatus === 'paused' ? 0 : 1;
      if (aPrio !== bPrio) return aPrio - bPrio;

      const aRenewal = a.renewalDate ? Date.parse(a.renewalDate) : 0;
      const bRenewal = b.renewalDate ? Date.parse(b.renewalDate) : 0;
      return bRenewal - aRenewal;
    });
}

/** Picks the subscription to show in account overview UI (first of {@link listVisibleBffSubscriptions}). */
export function selectPrimaryBffSubscription(
  subscriptions: BffSubscription[] | undefined,
): BffSubscription | undefined {
  return listVisibleBffSubscriptions(subscriptions).at(0);
}

/** Whether the subscription is still within its trial period. */
export function isBffSubscriptionInTrial(
  subscription: Pick<BffSubscription, 'trialEndDate'>,
  now: number = Date.now(),
): boolean {
  const trialEndDate = subscription.trialEndDate?.trim();
  if (!trialEndDate) {
    return false;
  }

  const trialEndMs = Date.parse(trialEndDate);
  if (Number.isNaN(trialEndMs)) {
    return false;
  }

  return trialEndMs > now;
}

function normalizedSubscriptionInterval(
  subscription: Pick<BffSubscription, 'interval'>,
): string {
  return String(normalizeBffSubscriptionInterval(String(subscription.interval)));
}

/** Whether the subscription bills on a yearly contract interval. */
export function isBffSubscriptionYearly(
  subscription: Pick<BffSubscription, 'interval'>,
): boolean {
  return normalizedSubscriptionInterval(subscription) === 'yearly';
}

/** Whether the subscription bills on a monthly contract interval. */
export function isBffSubscriptionMonthly(
  subscription: Pick<BffSubscription, 'interval'>,
): boolean {
  return normalizedSubscriptionInterval(subscription) === 'monthly';
}

/** Whether the current user may change, pause, or cancel this subscription in account UI. */
export function canManageBffSubscription(subscription: BffSubscription): boolean {
  if (isBffSubscriptionCancelled(subscription)) {
    return false;
  }

  const manageableFlag =
    subscription.isUserManageable ?? subscription.isUserManagable;

  if (manageableFlag !== undefined) {
    return manageableFlag;
  }

  const status = normalizeBffSubscriptionStatus(String(subscription.status));

  return (
    status === 'active' ||
    status === 'paused' ||
    status === 'paymentfailed' ||
    status === 'payment_failed'
  );
}

/**
 * Reads a BFF action permission. The BFF is authoritative when it sends `userActions`;
 * `fallback` covers older payloads that omit the block.
 */
export function isBffSubscriptionActionAllowed(
  subscription: Pick<BffSubscription, 'userActions'>,
  action: BffSubscriptionUserActionName,
  fallback: boolean,
): boolean {
  const permission = subscription.userActions?.[action];

  return permission ? permission.allowed : fallback;
}

/** Denial reason the BFF gave for an action, for messaging or debugging. */
export function bffSubscriptionActionDeniedReason(
  subscription: Pick<BffSubscription, 'userActions'>,
  action: BffSubscriptionUserActionName,
): string | null {
  const permission = subscription.userActions?.[action];

  return permission && !permission.allowed ? (permission.reason ?? null) : null;
}

export function canPauseBffSubscription(subscription: BffSubscription): boolean {
  return isBffSubscriptionActionAllowed(
    subscription,
    'pause',
    canManageBffSubscription(subscription) &&
      !isBffSubscriptionInTrial(subscription),
  );
}

export function canResumeBffSubscription(
  subscription: BffSubscription,
): boolean {
  return isBffSubscriptionActionAllowed(
    subscription,
    'resume',
    canManageBffSubscription(subscription) &&
      normalizeBffSubscriptionStatus(String(subscription.status)) === 'paused',
  );
}

export function canCancelBffSubscription(
  subscription: BffSubscription,
): boolean {
  return isBffSubscriptionActionAllowed(
    subscription,
    'cancel',
    canManageBffSubscription(subscription),
  );
}

export function canInviteBffSubscriptionMember(
  subscription: BffSubscription,
): boolean {
  return isBffSubscriptionActionAllowed(
    subscription,
    'inviteMember',
    (subscription.seats?.available ?? 0) > 0,
  );
}

export type BffSubscriptionErrorCode =
  | 'CannotPausePermanentSubscription'
  | 'CannotResumePermanentSubscription'
  | 'CannotCancelPermanentSubscription'
  | 'InvalidSubscriptionStatus';

export type BffSubscriptionErrorResponse = {
  error: {
    code: BffSubscriptionErrorCode;
    message: string;
  };
};
